import React, {useEffect, useMemo, useState} from 'react';
import {Badge, Box, IconButton as MuiIconButton} from "@mui/material";
import {CancelRounded, CloseRounded, FilterListFilledOutlined, SearchRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import {IconButton, Input} from "@mui/joy";
import _ from 'lodash';
import {focusFirstItem, getColumnEl} from "../../utils/installedBaseKeyboardNav";

interface Props {
    searchQuery: string;
    onSearch: (query: string) => void;
    isFilterOpen: boolean;
    onShowFilterContent: (value: boolean) => void;
    activeFilterCount?: number;
}

const InstalledBaseSearchFilter = ({searchQuery, onSearch, isFilterOpen, onShowFilterContent, activeFilterCount}: Props) => {
    const {t} = useTranslation();
    const [inputValue, setInputValue] = useState(searchQuery);

    useEffect(() => {
        setInputValue(searchQuery);
    }, [searchQuery]);

    const debouncedSearch = useMemo(() => _.debounce(onSearch, 700), [onSearch]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedSearch(value);
    };

    const handleClear = () => {
        debouncedSearch.cancel();
        setInputValue("");
        onSearch("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            focusFirstItem(getColumnEl(e.currentTarget));
        } else if (e.key === 'Enter') {
            debouncedSearch.flush();
        }
    };

    return (
        <Box display={'flex'} flexDirection={'row'} gap={1}>
            <Input
                startDecorator={<SearchRounded />}
                endDecorator={
                    <MuiIconButton
                        size="small"
                        onClick={handleClear}
                        aria-label={t("Clear search")}
                        sx={{
                            p: 0.5,
                            visibility: inputValue ? 'visible' : 'hidden',
                            color: 'neutral.500',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: 'neutral.700',
                            },
                        }}
                    >
                        <CancelRounded fontSize="small" />
                    </MuiIconButton>
                }
                placeholder={t("Search")}
                name="query"
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                // Browser-Autofill unterdrücken: dessen Dropdown fängt sonst
                // ArrowDown ab (Sprung in die Liste) und ist per JS weder
                // erkennbar noch steuerbar.
                slotProps={{ input: { autoComplete: 'off' } }}
                sx={{
                    padding: '0 0 0 0.75rem',
                    height: '34px',
                    background: 'transparent',
                    flexGrow: 1,
                }}
            />
            {
                activeFilterCount ?
                    <Badge badgeContent={activeFilterCount} color={"secondary"}>
                        <IconButton variant="outlined" onClick={() => onShowFilterContent(!isFilterOpen)}>
                            {
                                isFilterOpen ? <CloseRounded /> : <FilterListFilledOutlined />
                            }
                        </IconButton>

                    </Badge>
                :
                    <IconButton variant="outlined" onClick={() => onShowFilterContent(!isFilterOpen)}>
                        {
                            isFilterOpen ? <CloseRounded /> : <FilterListFilledOutlined />
                        }
                    </IconButton>
            }
        </Box>
    );
};

export default InstalledBaseSearchFilter;
